/**
 * Customer site governance permissions.
 * Server enforcement is authoritative; UI hiding is not authorization.
 */

import { normalizeUserRole } from "./userRoles";

export type CustomerSiteActor = {
  id: number;
  role: string;
  name?: string | null;
};

export type CustomerSiteOwner = {
  salespersonId?: number | null;
};

/** View stored service sites using staff customer read visibility (PIC does not hide sites). */
export function canViewCustomerSites(actor: CustomerSiteActor, customer: CustomerSiteOwner): boolean {
  const role = normalizeUserRole(actor.role);
  if (role === "admin" || role === "finance" || role === "service") return true;
  if (role === "sales") return true;
  return role === "installer" || role === "programming";
}

/**
 * Manage (add/edit/archive/restore/set default) customer sites.
 * Admin: any visible customer. Current PIC salesperson: own customer.
 * No PIC: Admin only. Service/Finance/installer/programming: never via this workflow.
 */
export function canManageCustomerSites(actor: CustomerSiteActor, customer: CustomerSiteOwner): boolean {
  const role = normalizeUserRole(actor.role);
  if (role === "admin") return true;
  if (role === "sales") {
    return customer.salespersonId != null && customer.salespersonId === actor.id;
  }
  return false;
}

export function assertCanManageCustomerSites(
  actor: CustomerSiteActor,
  customer: CustomerSiteOwner,
): void {
  if (!canManageCustomerSites(actor, customer)) {
    const err = new Error("Not authorised to manage this customer's service sites.");
    (err as Error & { code: string }).code = "FORBIDDEN";
    throw err;
  }
}
